import type { Melody } from '@tunasong/schemas'
import { melodyDurationToVexFlow } from '../note/melody-duration.js'
import { DURATION_BEATS, melodyToSuggestion } from './convert.js'
import { barBeats, GRID_TOLERANCE } from './grid.js'

/**
 * Flatten a melody into an EasyScore `lang` string for an ADDITIVE staff sibling (§3).
 *
 * The sibling is a VIEW: the melody stays the source of truth, and the flattening is lossy on
 * purpose. Every loss is reported rather than swallowed, so the command can tell the user what
 * the staff will not show:
 *
 *   - `offsets-dropped` - notation has no way to draw "12 ms late".
 *   - `duration-rounded` - EasyScore's duration tokens are w/h/q/8/16; a triplet is rounded.
 *   - `rest-dropped`    - a gap between notes has no note to hang on, and EasyScore rests are not
 *     modelled here. The staff therefore looks rhythmically denser than the melody sounds.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §3
 */

export interface MelodyRenderLoss {
  kind: 'offsets-dropped' | 'duration-rounded' | 'rest-dropped' | 'out-of-range'
  noteIndex?: number
  message: string
}

export const melodyToScoreLang = (melody: Melody): { lang: string; losses: MelodyRenderLoss[] } => {
  const losses: MelodyRenderLoss[] = []
  if (melody.notes.length === 0) {
    return { lang: '', losses }
  }

  if (melody.notes.some(n => Math.round(Math.abs(n.offsetMs)) > 0)) {
    losses.push({
      kind: 'offsets-dropped',
      message: 'The staff cannot show the timing offsets - the melody keeps them.',
    })
  }

  const suggestion = melodyToSuggestion(melody)
  suggestion.forEach((s, index) => {
    const source = melody.notes[index]
    if (Math.abs(DURATION_BEATS[s.duration] - source.duration) > GRID_TOLERANCE) {
      losses.push({
        kind: 'duration-rounded',
        noteIndex: index,
        message: `Note ${index + 1}: ${source.duration} beats drawn as ${s.duration}.`,
      })
    }
    const previous = melody.notes[index - 1]
    if (previous && source.onset - (previous.onset + previous.duration) > GRID_TOLERANCE) {
      losses.push({
        kind: 'rest-dropped',
        noteIndex: index,
        message: `Note ${index + 1}: the rest before it is not drawn.`,
      })
    }
  })

  const perBar = barBeats(melody.tempo.meter)
  const bars: string[][] = []
  suggestion.forEach((s, index) => {
    const bar = Math.floor(melody.notes[index].onset / perBar)
    bars[bar] = [...(bars[bar] ?? []), `${s.pitch}${s.octave}/${melodyDurationToVexFlow(s.duration)}`]
  })

  return {
    lang: bars
      .filter(Boolean)
      .map(bar => bar.join(', '))
      .join('\n'),
    losses,
  }
}
